import React from 'react';
import logo from '../assets/logo.png';
import { COMPANY, SOCIAL } from '../config';

const FOOTER_LINKS = [
  {
    title: 'Company',
    links: [
      { name: 'About Us', href: '#about' },
      { name: 'Our Services', href: '#services' },
      { name: 'Careers', href: '#careers' },
      { name: 'Products', href: '#products' },
      { name: 'Portfolio', href: '#portfolio' },
    ],
  },
  {
    title: 'Services',
    links: [
      { name: 'AI Development', href: '#services' },
      { name: 'Web Development', href: '#services' },
      { name: 'Mobile Apps', href: '#services' },
      { name: 'Consulting', href: '#services' },
    ],
  },
  {
    title: 'Products',
    links: [
      { name: 'FitPulseBot', href: '#products' },
      { name: 'AI Visual Teacher', href: '#products' },
      { name: 'AI Marketing SaaS', href: '#products' },
      { name: 'Upcoming Products', href: '#products' },
    ],
  },
];

// Only show social links that have a real URL (not '#')
const ALL_SOCIAL = [
  {
    name: 'LinkedIn',
    href: SOCIAL.LINKEDIN,
    icon: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
  },
  {
    name: 'Twitter / X',
    href: SOCIAL.TWITTER,
    icon: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
  },
  {
    name: 'GitHub',
    href: SOCIAL.GITHUB,
    icon: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>,
  },
];

// Filter out placeholder '#' links
const ACTIVE_SOCIAL = ALL_SOCIAL.filter(s => s.href && s.href !== '#');

const Footer = () => {
  const year = new Date().getFullYear();

  const scrollTo = (href) => {
    if (href.startsWith('#')) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-brand-bg border-t border-brand-border relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[200px] rounded-full bg-brand-blue/4 blur-[100px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">

          {/* Brand col */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-1 mb-4">
              <img src={logo} alt="Avarnix" className="h-[6.3rem] w-auto -mr-3" />
              <div>
                <div className="text-lg font-bold text-brand-text">
                  <span className="text-gradient">Avarnix</span> Technologies
                </div>
                <div className="text-xs text-brand-muted">Where Ideas Meet AI</div>
              </div>
            </div>
            <p className="text-brand-muted text-sm leading-relaxed mb-6 max-w-xs">
              A product-based AI and software company headquartered in Bangalore. We build intelligent products and deliver custom solutions for measurable business outcomes.
            </p>

            {/* Social icons — only rendered if URL is set */}
            <div className="flex items-center gap-3 flex-wrap">
              {ACTIVE_SOCIAL.length > 0
                ? ACTIVE_SOCIAL.map((s, i) => (
                    <a key={i} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.name}
                      className="w-9 h-9 glass rounded-lg flex items-center justify-center text-brand-muted hover:text-brand-blue hover:border-brand-blue/30 border border-brand-border transition-all duration-200">
                      {s.icon}
                    </a>
                  ))
                : <p className="text-brand-dim text-xs italic">Social links coming soon</p>
              }
              <a href={`tel:${COMPANY.PHONE.replace(/\s+/g, '')}`}
                className="w-9 h-9 glass rounded-lg flex items-center justify-center text-brand-muted hover:text-brand-blue hover:border-brand-blue/30 border border-brand-border transition-all duration-200 text-sm"
                aria-label="Call us">📞</a>
              <a href={`mailto:${COMPANY.EMAIL}`}
                className="w-9 h-9 glass rounded-lg flex items-center justify-center text-brand-muted hover:text-brand-blue hover:border-brand-blue/30 border border-brand-border transition-all duration-200 text-sm"
                aria-label="Email us">✉️</a>
            </div>
          </div>

          {/* Nav cols */}
          {FOOTER_LINKS.map((section, si) => (
            <div key={si}>
              <h4 className="text-brand-blue font-semibold text-sm mb-4 uppercase tracking-wider">{section.title}</h4>
              <ul className="space-y-3">
                {section.links.map((link, li) => (
                  <li key={li}>
                    <button onClick={() => scrollTo(link.href)}
                      className="text-brand-muted hover:text-brand-text text-sm transition-colors duration-150 text-left">
                      {link.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* CTA strip */}
        <div className="glass rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-5 border border-brand-border mb-10">
          <div>
            <h4 className="text-brand-text font-bold text-lg mb-1">Ready to Start Your AI Journey?</h4>
            <p className="text-brand-muted text-sm">Get in touch for a free consultation.</p>
          </div>
          <button onClick={() => scrollTo('#contact')} className="btn-primary whitespace-nowrap px-8 py-3">
            Get Free Consultation
          </button>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-brand-border py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-brand-dim">
          <div>© {year} Avarnix Technologies. All rights reserved.</div>
          <div className="flex gap-6">
            <button className="hover:text-brand-muted transition-colors">Privacy Policy</button>
            <button className="hover:text-brand-muted transition-colors">Terms & Conditions</button>
          </div>
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="w-9 h-9 glass rounded-lg border border-brand-border flex items-center justify-center text-brand-muted hover:text-brand-blue hover:border-brand-blue/30 transition-all"
            aria-label="Back to top">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
